import { useEffect, useRef, useState } from "react";

/**
 * useDraft — lightweight auto-save for text composers.
 *
 * Returns [draftState, setDraftState, { saved, clear, hasDraft }].
 *
 * draftState is an object you fully control; whenever it changes, the hook
 * debounces (default 700ms) and persists it under `pgd.draft.<key>` in
 * localStorage. On mount it hydrates any previously saved draft. Call
 * `clear()` after a successful send so the slot doesn't leak.
 */
export default function useDraft(key, initial, debounceMs = 700) {
  const storageKey = `pgd.draft.${key}`;
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...initial, ...parsed };
      }
    } catch (_e) { /* ignore */ }
    return initial;
  });
  const [saved, setSaved] = useState(false);
  const timer = useRef(null);
  const first = useRef(true);

  useEffect(() => {
    // Skip persisting on the very first render — avoids re-saving the restored
    // draft back to storage and never fires the "saved" flag until the user
    // actually edits something.
    if (first.current) { first.current = false; return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try {
        const hasContent = Object.values(state).some(
          v => typeof v === "string" ? v.trim().length > 0 : !!v
        );
        if (hasContent) {
          localStorage.setItem(storageKey, JSON.stringify(state));
          setSaved(true);
          // Hide "Saved" after a moment so it feels like a transient toast
          setTimeout(() => setSaved(false), 2000);
        } else {
          localStorage.removeItem(storageKey);
          setSaved(false);
        }
      } catch (_e) { /* storage might be unavailable (private mode) */ }
    }, debounceMs);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(state)]);

  const clear = () => {
    try { localStorage.removeItem(storageKey); } catch (_e) { /* ignore */ }
    setSaved(false);
    setState(initial);
  };

  const hasDraft = (() => {
    try { return !!localStorage.getItem(storageKey); }
    catch (_e) { return false; }
  })();

  return [state, setState, { saved, clear, hasDraft }];
}
